import React, { useState, useCallback, useEffect } from "react";
import Managed, { useManaged } from "../../common/components/Managed";
import moment from "moment";

import {
  Button,
  Tag,
  Intent,
  Drawer,
  Classes,
  Tabs,
  Tab,
  Collapse,
} from "@blueprintjs/core";

import api from "../../common/lib/api";
import YAML from "yaml";
import { extractSingleError } from "../../common/components/Errors";
import AsyncButton from "../../common/components/AsyncButton";

const DEFAULT_COMMANDS = [];

export function CommandBlock({
  endpoint,
  enabled,
  extraFields,
  summarize,
  schedule,
}) {
  const [commands, setCommands, setError] = useManaged(DEFAULT_COMMANDS);

  const [visible, setVisible] = useState(!Boolean(summarize));

  const reloadCommands = useCallback(async () => {
    try {
      const { data } = await api.get(endpoint + "/list");
      setCommands(data.data);
    } catch (e) {
      setError(e);
    }
  }, [endpoint, setCommands, setError]);

  const scheduleAll = useCallback(async () => {
    await api.post(endpoint + "/schedule-all", {});
    reloadCommands();
  }, [endpoint, reloadCommands]);

  const retryAll = useCallback(async () => {
    await api.post(endpoint + "/retry-all", {});
    reloadCommands();
  }, [endpoint, reloadCommands]);

  const rerunAll = useCallback(async () => {
    await api.post(endpoint + "/rerun-all", {});
    reloadCommands();
  }, [endpoint, reloadCommands]);

  useEffect(() => {
    if (enabled) {
      reloadCommands();
    }
    const timer = setInterval(() => {
      if (enabled) {
        reloadCommands();
      }
    }, 30e3);
    return () => {
      clearInterval(timer);
    };
  }, [enabled, reloadCommands]);

  const canRefresh = Boolean(reloadCommands);

  const shouldShowBulkActions = canRefresh; // || canSchedule || canReschedule  || hasSucceeded;

  const children = [];

  const summary = {};

  for (const cmd of commands.data) {
    const [status, statusIntent] = getStatus(cmd.command);

    summary[status] = summary[status] || 0;
    summary[status] += 1;

    children.push(
      <CommandBlockItem
        {...cmd}
        extraFields={extraFields}
        status={status}
        statusIntent={statusIntent}
        onRefresh={reloadCommands}
        schedule={schedule}
      />
    );
  }

  const hasNotScheduled = Boolean(summary[STATE_NOT_SCHEDULED]);
  const hasFailed = Boolean(summary[STATE_FAILED]);
  const hasSucceeded = Boolean(summary[STATE_SUCCESSFUL]);
  const canSchedule = schedule && hasNotScheduled;
  const canReschedule = schedule && hasFailed;
  const canRerun = schedule && hasSucceeded;

  const actions = (
    <>
      {shouldShowBulkActions && (
        <p>
          {canSchedule && (
            <AsyncButton small onClick={scheduleAll}>
              Schedule All
            </AsyncButton>
          )}
          {canReschedule && (
            <AsyncButton small onClick={retryAll}>
              Retry All Failed
            </AsyncButton>
          )}
          {canRerun && (
            <AsyncButton small onClick={rerunAll}>
              Rerun All
            </AsyncButton>
          )}
          {canRefresh && (
            <Button small onClick={reloadCommands}>
              Refresh
            </Button>
          )}
          {summarize && visible && (
            <Button small onClick={() => setVisible(false)}>
              Hide
            </Button>
          )}
          {summarize && !visible && (
            <Button small onClick={() => setVisible(true)}>
              Show
            </Button>
          )}
        </p>
      )}
    </>
  );

  if (!visible) {
    const labels = [];
    for (const [status, count] of Object.entries(summary)) {
      const [statusText, intent] = getStatus({ status });
      labels.push(
        <Tag intent={intent}>
          {statusText} x {count}
        </Tag>
      );
    }

    return (
      <>
        {actions}
        <p>{labels}</p>
      </>
    );
  }

  return (
    <Managed state={commands}>
      {actions}
      <table className="bp3-html-table bp3-html-table-condensed bp3-html-table-bordered bp3-interactive">
        <thead>
          <tr>
            <th colSpan="2">Item / Step</th>
            <th>Updated</th>
            {extraFields &&
              extraFields.map((field) => (
                <th key={`extra-head-${field.label}`}>{field.label}</th>
              ))}
            <th className="print-hide">Actions</th>
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </Managed>
  );

  function CommandBlockItem({
    step,
    command,
    title,
    type,
    state,
    value,
    status,
    statusIntent,
    schedule,
    onRefresh,
    extraFields,
  }) {
    const [fromDb, setFromDb] = useState(null);
    const [detailsVisible, setDetailsVisible] = useState(false);

    if (fromDb) {
      command = fromDb;
    }

    const uuid = command && command.uuid;

    const doSchedule = async () => {
      try {
        await api.post(endpoint + "/schedule-one", { type, value });
        onRefresh();
      } catch (e) {
        console.log(e);
        alert(extractSingleError(e));
      }
    };

    const doRetry = async () => {
      try {
        await api.post(endpoint + "/retry-one", { type, value });
        onRefresh();
      } catch (e) {
        console.log(e);
        alert(extractSingleError(e));
      }
    };

    const doCancel = async () => {
      try {
        await api.post(endpoint + "/cancel-one", { uuid });
        onRefresh();
      } catch (e) {
        console.log(e);
        alert(extractSingleError(e));
      }
    };

    useEffect(() => {
      if (detailsVisible && uuid) {
        api
          .get(endpoint + "/details/" + uuid)
          .then(({ data }) => setFromDb(data.data));
      }
      const interval = setInterval(async () => {
        if (detailsVisible && uuid) {
          const { data } = await api.get(endpoint + "/details/" + uuid);
          setFromDb(data.data);
        } else {
          setFromDb(null);
        }
      }, 5e3);

      return () => {
        clearInterval(interval);
      };
    }, [detailsVisible, uuid]);

    const updated = command && moment(command.updatedAt || command.createdAt);

    const statusTag = <Tag intent={statusIntent}>{status}</Tag>;

    let actionsTag = (
      <>
        {schedule && [STATE_NOT_SCHEDULED].includes(status) && (
          <Button small="true" onClick={doSchedule}>
            Schedule
          </Button>
        )}
        {status === STATE_QUEUED && (
          <Button small="true" onClick={doCancel}>
            Cancel
          </Button>
        )}
        {schedule && [STATE_FAILED, STATE_CANCELLED].includes(status) && (
          <Button small="true" onClick={doRetry}>
            Retry
          </Button>
        )}
        {schedule && status === STATE_SUCCESSFUL && (
          <Button small="true" onClick={doRetry}>
            Rerun
          </Button>
        )}
      </>
    );
    let actions = (
      <>
        {actionsTag}
        {status !== STATE_NOT_SCHEDULED && (
          <Button
            small="true"
            onClick={() => {
              setDetailsVisible(true);
              // onRefresh();
            }}
          >
            Details
          </Button>
        )}
        {detailsVisible && (
          <Drawer
            isOpen="true"
            onClose={() => setDetailsVisible(false)}
            title={
              <span>
                {command.name} {statusTag} {actionsTag}
              </span>
            }
            size={Drawer.SIZE_LARGE}
          >
            <div className={Classes.DRAWER_BODY}>
              <div className={Classes.DIALOG_BODY}>
                <Tabs>
                  <Tab
                    id="dates"
                    title="Dates"
                    panel={
                      <>
                        <p>Created: {formatDateHelper(command.createdAt)}</p>
                        <p>
                          Last Updated: {formatDateHelper(command.updatedAt)}
                        </p>
                        <p>Scheduled: {formatDateHelper(command.scheduleAt)}</p>
                      </>
                    }
                  />
                  <Tab
                    id="state"
                    title="Internal"
                    panel={
                      <>
                        <pre className="bp3-text-small">
                          {YAML.stringify(command.state)}
                        </pre>
                      </>
                    }
                  />
                </Tabs>
                {command.events && (
                  <CommandActivityLog events={command.events} />
                )}
              </div>
            </div>
          </Drawer>
        )}
      </>
    );

    return (
      <tr>
        <td>{step}</td>
        <td>
          {command ? command.name : title} {statusTag}
        </td>
        <td title={command ? updated.format("llll") : "never"}>
          {command ? updated.fromNow() : "never"}
        </td>
        {extraFields &&
          extraFields.map((field) => (
            <td key={`extra-val-${field.label}-${command && command.uuid}`}>
              {command && field.render(command)}
            </td>
          ))}
        <td className="print-hide">{actions}</td>
      </tr>
    );
  }
}
function formatDateHelper(date) {
  return date ? moment(date).format("llll") : "no date";
}

const STATE_NOT_SCHEDULED = "not scheduled";
const STATE_QUEUED = "queued";
const STATE_FAILED = "failed";
const STATE_CANCELLED = "cancelled";
const STATE_SUCCESSFUL = "successful";
const STATE_RUNNING = "running";

function getStatus(command) {
  if (!command) {
    return [STATE_NOT_SCHEDULED, Intent.NONE];
  }

  if (command.status === STATE_QUEUED) {
    return [command.status, Intent.PRIMARY];
  }

  if (command.status === STATE_FAILED) {
    return [command.status, Intent.DANGER];
  }

  if (command.status === STATE_CANCELLED) {
    return [command.status, Intent.NONE];
  }

  if (command.status === STATE_RUNNING) {
    return [command.status, Intent.WARNING];
  }

  if (command.status === STATE_SUCCESSFUL) {
    return [command.status, Intent.SUCCESS];
  }

  return [command.status, Intent.NONE];
}

function CommandActivityLog({ events }) {
  const [details, setDetails] = useState(null);
  return (
    <>
      <h3>Activity</h3>
      <table className="bp3-html-table bp3-small bp3-interactive">
        <tbody>
          {events.map((event) => {
            const time = moment(event.createdAt);
            const data = JSON.parse(event.data);
            let body = <pre>{YAML.stringify(event)}</pre>;
            if (event.type === "running") {
              body = `Started running on ${data.runNode}`;
            }
            if (event.type === "exception") {
              body = <pre>{data}</pre>;
            }
            if (event.type === "successful") {
              body = `Took ${data.durationMs}ms`;
            }

            if (event.type === "infoLog") {
              body = data.message;
            }

            if (event.type === "errorLog") {
              body = data.message;
            }

            if (data.extra) {
              body = (
                <>
                  {body}
                  {details !== event.commandEventId && (
                    <>
                      {" "}
                      <Button onClick={() => setDetails(event.commandEventId)}>
                        Show more
                      </Button>
                    </>
                  )}
                  <Collapse isOpen={details === event.commandEventId}>
                    {" "}
                    <Button onClick={() => setDetails("")}>Show less</Button>
                    <pre className="bp3-text-small">
                      <CommandActivityLogExtra extra={data.extra} />
                    </pre>
                  </Collapse>
                </>
              );
            }

            return (
              <tr key={event.commandEventId}>
                <th title={time.format("llll")}>{time.fromNow()}</th>
                <td>{event.type}</td>
                <td>{body}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

function CommandActivityLogExtra({ extra }) {
  const keysCount = Object.keys(extra).length;
  const { invoice } = extra || {};
  if (keysCount === 1 && invoice) {
    return (
      <>
        <h2>Invoice</h2>
        <table>
          {invoice.sections.map((section) => (
            <>
              {section.title && (
                <thead>
                  <tr>
                    <th>{section.title}</th>
                  </tr>
                </thead>
              )}
              <tbody>
                {section.lines.map((line) => (
                  <tr>
                    <td>
                      {line.item !== "Header" && (
                        <>
                          <strong>{line.item}</strong>
                          <br />
                        </>
                      )}
                      {line.description}
                    </td>
                    <td>
                      ${line.unitPrice} x {line.quantity}
                    </td>
                    <td>{line.discountPercent}%</td>
                  </tr>
                ))}
              </tbody>
            </>
          ))}
        </table>
        {/* {YAML.stringify(extra)} */}
      </>
    );
  }

  return YAML.stringify(extra);
}
